import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaSearch, FaEdit, FaTrash, FaGripHorizontal } from 'react-icons/fa';
import { 
  TextField, 
  IconButton,
  Box,
  InputAdornment,
  Typography,
  Chip
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, TemplateGrid, TemplateCard, TemplateCardContent, TemplateTitle, TemplateDetailsGrid, ItalicText, ActionButtons } from './styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import { competencyTemplatesApi } from '../../../services/api';
import { COLORS } from '../../../constants/radar';
import { SKILL_TYPES, getStatusColor, getStatusTextColor, getStatusText } from '../../../constants/skillTypes';

const CompetencyTemplates = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    template: null,
    formData: { name: '', type: 'Technical' }
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, template: null });

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await competencyTemplatesApi.getAllTemplates();
      setTemplates(data);
    } catch (err) {
      showAlert('Failed to load competency templates.', 'error', err);
      console.error('Error fetching competency templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleModalOpen = (mode, template = null) => {
    setModalState({
      isOpen: true,
      mode,
      template,
      formData: template ? {
        name: template.name,
        type: template.type
      } : { name: '', type: 'Technical' }
    });
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'add',
      template: null,
      formData: { name: '', type: 'Technical' }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalState(prev => ({
      ...prev,
      formData: { ...prev.formData, [name]: value }
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!modalState.formData.name.trim()) {
        showAlert('Template name is required', 'error');
        return;
      }

      if (modalState.mode === 'add') {
        const response = await competencyTemplatesApi.createTemplate(modalState.formData);
        showAlert('Competency template created successfully', 'success');
      } else {
        const response = await competencyTemplatesApi.updateTemplate(modalState.template.id, modalState.formData);
        showAlert('Competency template updated successfully', 'success');
      }
      handleModalClose();
      fetchTemplates();
    } catch (err) {
      console.error('Error in template submission');
      showAlert(`Failed to ${modalState.mode === 'add' ? 'create' : 'update'} competency template. ${err.message || 'Unknown error'}`, 'error', err);
    }
  };

  const handleDeleteTemplate = (template) => {
    setDeleteModal({ open: true, template });
  };

  const confirmDelete = async () => {
    try {
      await competencyTemplatesApi.deleteTemplate(deleteModal.template.id);
      showAlert('Competency template deleted successfully', 'success');
      setDeleteModal({ open: false, template: null });
      fetchTemplates();
    } catch (err) {
      showAlert(`Failed to delete competency template. ${err.message}`, 'error', err);
    }
  };

  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix')}>
            <FaArrowLeft /> Back to Skills Matrix
          </BackButton>
          <Title>Competency Templates</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search templates..."
              variant="outlined"
              size="small"
              value={searchTerm}
              autoComplete="off"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                flex: 1,
                maxWidth: '400px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.PRIMARY,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.PRIMARY,
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />
            
            <HeaderButton 
              onClick={() => handleModalOpen('add')}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: COLORS.PRIMARY,
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Template
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredTemplates.length}
                </span>
                {filteredTemplates.length === 1 ? 'template' : 'templates'} found
              </div>
              {filteredTemplates.length === 0 ? (
                <EmptyState 
                  title="No competency templates found"
                  description={searchTerm ? 
                    "No templates match your search criteria. Try adjusting your search term." : 
                    "No competency templates have been created yet. Click the '+ Add Template' button to create your first template."}
                />
              ) : (
                <TemplateGrid>
                  {filteredTemplates.map(template => (
                    <TemplateCard 
                      key={template.id}
                    >
                      <TemplateCardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <TemplateTitle>{template.name}</TemplateTitle>
                          <Chip 
                            label={template.type} 
                            size="small" 
                            sx={{ 
                              backgroundColor: getStatusColor(template.type),
                              color: getStatusTextColor(template.type),
                              fontWeight: 500,
                              border: '1px solid',
                              borderColor: `${getStatusTextColor(template.type)}20`
                            }} 
                          />
                        </Box>
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                            <strong>Levels</strong>
                          </Typography>
                          {template.levels.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {/* Sort levels by level number */}
                              {template.levels.sort((a, b) => a.level - b.level).map(level => (
                                <Box 
                                  key={level.id}
                                  sx={{ 
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: level.color || '#F8F9FA',
                                    transition: 'all 0.2s ease',
                                    width: '145px',
                                    opacity: 0.8,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: level.color || '#E5EDFF',
                                        border: '1px solid #333'
                                      }}
                                    />
                                    <Typography sx={{ 
                                      color: '#000',
                                      fontSize: '13px',
                                      fontWeight: 500
                                    }}>
                                      {level.name}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: COLORS.GRAY }}>
                              No levels defined
                            </Typography>
                          )}
                        </Box>

                        <ActionButtons onClick={(e) => e.stopPropagation()}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalOpen('edit', template);
                            }}
                            color="primary"
                          >
                            <FaEdit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template);
                            }}
                            color="error"
                          >
                            <FaTrash />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/skills-matrix/competency-templates/${template.id}`);
                            }}
                            color="primary"
                          >
                            <FaGripHorizontal />
                          </IconButton>
                        </ActionButtons>
                      </TemplateCardContent>
                    </TemplateCard>
                  ))}
                </TemplateGrid>
              )}
            </>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Template Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        title={modalState.mode === 'add' ? 'Add Competency Template' : 'Edit Competency Template'}
        onSubmit={handleSubmit}
        submitText={modalState.mode === 'add' ? 'Create' : 'Update'}
        submitDisabled={!modalState.formData.name.trim() || !modalState.formData.type}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            placeholder="Enter template name"
            value={modalState.formData.name}
            onChange={handleInputChange}
            autoComplete="new-password"

          />
          <TextField
            label="Type"
            name="type"
            select
            required
            fullWidth
            variant="outlined"
            value={modalState.formData.type}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="Technical">Technical</option>
            <option value="Soft">Soft</option>
          </TextField>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, template: null })}
        onConfirm={confirmDelete}
        title="Delete Competency Template"
        message={`Are you sure you want to delete the "${deleteModal.template?.name}" template? This action cannot be undone.`}
      />
    </Container>
  );
};

export default CompetencyTemplates; 